import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { UserContext } from '../../context/UserContext';
import { Link, useNavigate } from 'react-router-dom';
import cssClass from './RoadmapsLandingScreen.module.css';
import CustomizationModal from '../modals/CustomizationModal';
import Header from '../Header';
import Footer from '../Footer';
import Loader from '../Loader';
import begineerIcon from '../../assets/beginner-icon.svg';
import intermediateIcon from '../../assets/intermediate-icon.svg';
import advancedIcon from '../../assets/advanced-icon.svg';
import editIcon from '../../assets/edit-icon-white.svg';
import deleteIocn from '../../assets/delete-icon.svg';
import cloneIcon from '../../assets/clone-icon.svg';
import { AnimatePresence, motion } from 'framer-motion';
import { ADMIN_ROLE, changePageTitle } from '../../utils/constants';
import { useConfirm } from '../../context/ConfirmationContext';
import { v4 as uuidv4 } from 'uuid';
import { useNotification } from '../../hooks/useNotification';
import RoadmapAccessSharing from '../RoadmapAccessSharing';
import { substringSearch } from '../../utils/search';

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.4, ease: 'easeOut' },
  },
  hover: {
    // scale: 1.05,
    // borderColor: "rgba(255, 238, 0, 0.8)",
    // y: -5,
    // transition: {duration: 0.4}
  },
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
};

// Default search keys for roadmap
const DEFAULT_SEARCH_KEYS = ['title'];

/**
 * RoadmapsLandingScreen Component
 *
 * @description
 * This is the main dashboard component for roadmap management. It displays all roadmaps associated with the current user, including those created by the user, shared with them, and public ones. The component offers sectioned lists for "Draft", "Published", and "Assigned" roadmaps with filtering/search and CRUD operations for each section (as permitted by user role). It also provides:
 *  - A global roadmap search across all roadmaps.
 *  - Controls for creating a new roadmap (admin only).
 *  - Access management modal (admin only).
 *  - Responsive, animated UI with live updates on roadmap changes and modal management for clone and access dialogs.
 *
 * Major features:
 *   - Fetches, filters, and groups roadmaps by type/ownership.
 *   - Handles navigation to view/edit/create roadmaps.
 *   - Manages search and deletion of roadmaps with confirmation and notifications.
 *   - Renders key UI components such as Header, Footer, sectioned RoadmapSections, and loader states.
 *   - Uses AnimatePresence for animated modals and transitions.
 *
 * @returns {JSX.Element} The rendered dashboard landing page for roadmaps.
 */
const RoadmapsLandingScreen = () => {
  const {
    isLoading,
    userDetails,
    roadmaps,
    setRoadmaps,
    roadmapFetching,
    fetchAllRoadmapOfUser,
  } = useContext(UserContext);
  // const [roadmaps, setRoadmaps] = useState([]);
  const [selectedRoadmap, setSelectedRoadmap] = useState(null);
  const [activeButton, setActiveButton] = useState('Dashboard');
  const [showManageAccessModal, setShowManageAccessModal] = useState(false);
  // Global filtered roadmaps state
  const [globalFilteredRoadmaps, setGlobalFilteredRoadmaps] = useState(roadmaps);
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { showLoadingNotification, resolveNotification, rejectNotification } =
    useNotification();

  useEffect(() => {
    changePageTitle('Dashboard');
    if (!isLoading && userDetails?.email) {
      fetchAllRoadmapOfUser();
    }
  }, [isLoading, userDetails]);

  const handleRoadmapCardClick = (event, roadmap) => {
    event.stopPropagation();
    window.location.pathname = `/roadmap/${roadmap?.id}`;
  };

  const handleRoadmapEditClick = (event, roadmap) => {
    event.stopPropagation();
    navigate(`/draw-roadmap/${roadmap?.id}`);
  };

  const handleRoadmapCloneClick = (event, roadmap) => {
    event.stopPropagation();
    setSelectedRoadmap(roadmap);
  };

  const handleRoadmapDeleteClick = async (event, roadmap) => {
    event.stopPropagation();
    const confirmed = await confirm({
      type: 'delete-roadmap-confirmation',
      title: 'Are you sure you want to delete this roadmap ?',
      additionalProps: {
        cancelMessage: 'No, I don’t want to delete ',
        yesMessage: 'Yes, Delete the roadmap',
      },
    });
    if (confirmed) {
      const toastId = uuidv4();
      showLoadingNotification(toastId, 'Deleting Roadmap. Please wait');
      try {
        const payload = {
          e: userDetails?.email,
          r: roadmap?.id,
        };
        const response = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/r/remove-roadmap`,
          {
            method: 'DELETE',
            mode: 'cors',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          },
        );
        if (response.status === 200) {
          resolveNotification(toastId, 'Roadmap deleted successfully');
          setRoadmaps((prev) => prev.filter((r) => r.id !== roadmap.id));
        } else {
          rejectNotification(
            toastId,
            'Something went wrong. Please try again!',
          );
        }
      } catch (error) {
        console.log(error);
        rejectNotification(toastId, 'Something went wrong. Please try again!');
      }
    }
  };

  const formatText = (text) => {
    if (text.length > 1) {
      return text[0].toUpperCase() + text.slice(1);
    }
  };

  const getLevelIcon = (levelName) => {
    switch (levelName.toLowerCase()) {
      case 'begineer':
        return begineerIcon;
        break;
      case 'intermediate':
        return intermediateIcon;
        break;
      case 'advanced':
        return advancedIcon;
        break;
      default:
        return begineerIcon;
    }
  };

  const getFilteredRoadmaps = useCallback((section, currentRoadmaps) => {
    if (!currentRoadmaps || !userDetails) return [];

    switch (section) {
      case 'assigned':
        return currentRoadmaps.filter(
          (r) => r.access === 'viewer' && r.ownerEmail !== userDetails.email,
        );
      case 'published':
        return currentRoadmaps.filter(
          (r) =>
            ['owner', 'editor'].includes(r.access) && r.status === 'published',
        );
      case 'draft':
        return currentRoadmaps.filter(
          (r) => ['owner', 'editor'].includes(r.access) && r.status === 'draft',
        );

      default:
        return [];
    }
  }, [userDetails]);

  const draftRoadmaps = useMemo(() => getFilteredRoadmaps('draft', globalFilteredRoadmaps), [globalFilteredRoadmaps, getFilteredRoadmaps]);
  const publishedRoadmaps = useMemo(() => getFilteredRoadmaps('published', globalFilteredRoadmaps), [globalFilteredRoadmaps, getFilteredRoadmaps]);
  const assignedRoadmaps = useMemo(() => getFilteredRoadmaps('assigned', globalFilteredRoadmaps), [globalFilteredRoadmaps, getFilteredRoadmaps]);

  const onCancleManageAccess = () => {
    setShowManageAccessModal(false);
  };

  const handleGlobalFilterChange = useCallback((filtered) => {
    setGlobalFilteredRoadmaps(filtered);
  }, []);

  useEffect(() => {
    if (showManageAccessModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showManageAccessModal]);

  return (
    <div className={cssClass['roadmap-landing-screen']}>
      <Header />
      <div className={cssClass['header-next']}>
        <button
          className={`${
            activeButton === 'Dashboard'
              ? cssClass['header-next-btn-active']
              : cssClass['header-next-btn']
          }`}
        >
          Dashboard
        </button>
      </div>
      <div className={cssClass['main-container']}>
        <div className={cssClass['heading-div']}>
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{
              opacity: 1,
              scale: 1,
              y: 0,
              transition: { duration: 0.4, ease: 'easeOut' },
            }}
            className={cssClass['heading']}
          >
            Welcome, {formatText(userDetails?.email.split('@')[0])}!
          </motion.div>
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{
              opacity: 1,
              scale: 1,
              y: 0,
              transition: { duration: 0.4, ease: 'easeOut' },
            }}
            className={cssClass['sub-heading']}
          >
            Manage and track your roadmap creations seamlessly. Below, you'll
            find all the public roadmaps, roadmap created by you and shared with
            you. Stay organized and on track with roadmaps.
          </motion.div>
        </div>
        <div className={cssClass['roadmap-header']}>
          <div className={cssClass['roadmap-header-title']}>
            <span>Your Roadmaps</span>
            <RoadmapSearch roadmaps={roadmaps} onFilteredChange={handleGlobalFilterChange} placeholder='Search all roadmaps. . .' />
            </div>


          {userDetails?.role && userDetails?.role === ADMIN_ROLE && (
            <div className={cssClass['roadmap-header-btn']}>
              <Link
                to="/draw-roadmap/create"
                className={cssClass['roadmap-create-btn']}
              >
                New Roadmap
              </Link>
              <div
                // to="/r/access-sharing"
                className={cssClass['roadmap-access-btn']}
                onClick={() => setShowManageAccessModal(!showManageAccessModal)}
              >
                Manage Access
              </div>
            </div>
          )}
        </div>
        {roadmapFetching ? (
          <div className={cssClass['loader-div']}>
            <Loader height={80} width={80} />
          </div>
        ) : (
          <>
            {userDetails.role === ADMIN_ROLE && (
              <>
                <RoadmapSection
                  title="Draft"
                  roadmaps={draftRoadmaps}
                  userDetails={userDetails}
                  onCardClick={handleRoadmapCardClick}
                  onEditClick={handleRoadmapEditClick}
                  onCloneClick={handleRoadmapCloneClick}
                  onDeleteClick={handleRoadmapDeleteClick}
                />
                <RoadmapSection
                  title="Published"
                  roadmaps={publishedRoadmaps}
                  userDetails={userDetails}
                  onCardClick={handleRoadmapCardClick}
                  onEditClick={handleRoadmapEditClick}
                  onCloneClick={handleRoadmapCloneClick}
                  onDeleteClick={handleRoadmapDeleteClick}
                />
              </>
            )}
            <RoadmapSection
              title="Assigned"
              roadmaps={assignedRoadmaps}
              userDetails={userDetails}
              onCardClick={handleRoadmapCardClick}
              onEditClick={handleRoadmapEditClick}
              onCloneClick={handleRoadmapCloneClick}
              onDeleteClick={handleRoadmapDeleteClick}
            />
          </>
        )}
      </div>
      <Footer />
      <AnimatePresence>
        {selectedRoadmap && (
          <CustomizationModal
            roadmap={selectedRoadmap}
            setSelectedRoadmap={setSelectedRoadmap}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showManageAccessModal && (
          <RoadmapAccessSharing onCancel={onCancleManageAccess} />
        )}
      </AnimatePresence>
    </div>
  );
};

/**
 * RoadmapSection Component
 *
 * @description
 * This component is responsible for rendering a section of roadmaps grouped by type (such as Draft, Published, Assigned, etc.).
 * It displays a title, a search bar for filtering roadmaps within the section (if the user is an admin),
 * and a list of RoadmapCard components for each roadmap in the filtered list.
 * It provides section-level search and filtering, and passes through event handlers for card actions (click, edit, clone, delete).
 *
 * @param {Object} props
 * @param {string} props.title - The title of the roadmap section (e.g., "Draft", "Published", "Assigned").
 * @param {Array} props.roadmaps - The list of roadmap objects to display in this section.
 * @param {Object} props.userDetails - The details of the current user, to determine admin privileges.
 * @param {Function} props.onCardClick - Handler function for when a roadmap card is clicked.
 * @param {Function} props.onEditClick - Handler for when the edit action is triggered on a roadmap card.
 * @param {Function} props.onCloneClick - Handler for when the clone action is triggered on a roadmap card.
 * @param {Function} props.onDeleteClick - Handler for when the delete action is triggered on a roadmap card.
 *
 * @returns {JSX.Element} The rendered roadmap section including search (if admin), and filtered RoadmapCard list.
 */
const RoadmapSection = ({
  title,
  roadmaps,
  userDetails,
  onCardClick,
  onEditClick,
  onCloneClick,
  onDeleteClick,
}) => {

  // Section-level filtered roadmaps state
  const [sectionFilteredRoadmaps, setSectionFilteredRoadmaps] = useState(roadmaps);

  const handleSectionFilterChange = useCallback((filtered) => {
    setSectionFilteredRoadmaps(filtered);
  }, []);

  // Update section filtered roadmaps when roadmaps prop changes
  useEffect(() => {
    setSectionFilteredRoadmaps(roadmaps);
  }, [roadmaps]);

  return (
    <div className={cssClass['roadmap-section-ct']}>
      {userDetails.role == ADMIN_ROLE && (
        <>
          <div className={cssClass['roadmap-section-title']}>
          <span>{title}</span>
          
          <RoadmapSearch roadmaps={roadmaps} onFilteredChange={handleSectionFilterChange} placeholder={`Search roadmaps in ${title} section . . .`} />
        </div>
          <hr className={cssClass['roadmap-section-hr']} />
        </>
      )}
      {!sectionFilteredRoadmaps || sectionFilteredRoadmaps.length === 0 ? (
        <div className={cssClass['empty-roadmap']}>
          No {title.toLowerCase()} roadmaps found.
        </div>
      ) : (
        <motion.div
          className={cssClass['roadmaps-ct']}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {sectionFilteredRoadmaps.map((roadmap) => (
            <RoadmapCard
              key={roadmap.id}
              type={title}
              roadmap={roadmap}
              userDetails={userDetails}
              onCardClick={onCardClick}
              onEditClick={onEditClick}
              onCloneClick={onCloneClick}
              onDeleteClick={onDeleteClick}
            />
          ))}
        </motion.div>
      )}
    </div>
  );
};

/**
 * RoadmapCard Component
 *
 * @description
 * Renders a single roadmap card for display within the roadmaps dashboard or overview.
 * The card includes the roadmap's title, description, level, and available actions based
 * on user permissions and roadmap status. Administrative actions such as clone, edit,
 * and delete are selectively shown depending on the user's role and roadmap access rights.
 *
 * @param {Object} props
 * @param {string} props.type - The type/status of the roadmap (e.g., 'Draft', 'Published').
 * @param {Object} props.roadmap - The roadmap data object.
 * @param {Object} props.userDetails - The current user's details, used to determine access/role.
 * @param {Function} props.onCardClick - Handler function for clicking the roadmap card.
 * @param {Function} props.onEditClick - Handler for editing the roadmap (visible to editors/owners).
 * @param {Function} props.onCloneClick - Handler for cloning the roadmap (visible to admin).
 * @param {Function} props.onDeleteClick - Handler for deleting the roadmap (visible to owner only).
 *
 * @returns {JSX.Element} The rendered roadmap card component with title, description, level, and actions.
 */
const RoadmapCard = ({
  type,
  roadmap,
  userDetails,
  onCardClick,
  onEditClick,
  onCloneClick,
  onDeleteClick,
}) => {
  const getLevelIcon = (levelName) => {
    switch (levelName?.toLowerCase()) {
      case 'begineer':
        return begineerIcon;
      case 'intermediate':
        return intermediateIcon;
      case 'advanced':
        return advancedIcon;
      default:
        return begineerIcon;
    }
  };

  const formatText = (text) =>
    text?.length > 1 ? text[0].toUpperCase() + text.slice(1) : text;

  return (
    <motion.div
      className={cssClass['roadmap-card']}
      variants={cardVariants}
      whileHover="hover"
      onClick={(e) => onCardClick(e, roadmap)}
    >
      <div className={cssClass['card-head']}>
        <div className={cssClass['roadmap-title']} title={roadmap?.title}>
          {roadmap?.title}
        </div>
        {['Draft', 'Published'].includes(type) && (
          <RoadmapStatusBadge type={type} />
        )}
      </div>
      <div
        className={cssClass['roadmap-desc']}
        title={roadmap?.description || 'description'}
      >
        {roadmap?.description?.trim()
          ? roadmap.description.length > 200
            ? `${roadmap.description.substring(0, 200)}...`
            : roadmap.description
          : 'No description provided'}
      </div>
      <div className={cssClass['info-div']}>
        <div className={cssClass['info']}>
          <div className={cssClass['level-info']}>
            <img src={getLevelIcon(roadmap.level)} alt="" />
            <p>{formatText(roadmap.level)}</p>
          </div>
          {/* {
            userDetails?.role===ADMIN_ROLE && (
              <div className={cssClass['creator-info']}>
                <p className={cssClass['creator-text']}>Created by : </p>
                <p className={cssClass['creator-name']} >{formatText(roadmap.created_by.split('@')[0])}</p>
              </div>
            )} */}
        </div>
        <div className={cssClass['action-div']}>
          {userDetails?.role === ADMIN_ROLE && (
            <button
              className={cssClass['action-btn']}
              onClick={(e) => onCloneClick(e, roadmap)}
            >
              <img src={cloneIcon} alt="clone-icon" title="clone roadmap" />
            </button>
          )}
          {['editor', 'owner'].includes(roadmap?.access) && (
            <button
              className={cssClass['action-btn']}
              onClick={(e) => onEditClick(e, roadmap)}
            >
              <img src={editIcon} alt="edit-icon" title="edit roadmap" />
            </button>
          )}
          {roadmap?.access === 'owner' && (
            <button
              className={cssClass['action-btn']}
              onClick={(e) => onDeleteClick(e, roadmap)}
            >
              <img src={deleteIocn} alt="delete-icon" title="delete roadmap" />
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const RoadmapStatusBadge = ({ type }) => {
  return (
    <>
      {type === 'Draft' ? (
        <div className={cssClass['draft-badge']}>Draft</div>
      ) : (
        <div className={cssClass['published-badge']}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="9"
            height="9"
            viewBox="0 0 9 9"
            fill="none"
          >
            <circle cx="4.5" cy="4.5" r="4.5" fill="#3867F3" />
          </svg>
          <div className={cssClass['badge-text']}>Published</div>
        </div>
      )}
    </>
  );
};

/**
 * Generic RoadmapSearch Component
 * @param {Object} props
 * @param {Array} props.roadmaps - Array of roadmaps to filter
 * @param {Function} props.onFilteredChange - Callback function that receives filtered roadmaps
 * @param {string} props.placeholder - Placeholder text for the input
 * @param {string} props.className - Additional CSS classes
 * @param {Array} props.searchKeys - Keys to search in (default: ['title', 'description'])
 * @param {boolean} props.autoOpen - Whether to open search automatically (default: false)
 */
const RoadmapSearch = ({ 
  roadmaps = [],
  onFilteredChange,
  placeholder = "Search...",
  className = "",
  searchKeys = DEFAULT_SEARCH_KEYS,
  autoOpen = false
}) => {
  const [toggleSearch, setToggleSearch] = useState(autoOpen);
  const [query, setQuery] = useState('');

  const searchRef = useRef(null);

  // Filter roadmaps based on query
  const filteredRoadmaps = useMemo(() => {
    if (!query.trim()) return roadmaps;
    
    const results = substringSearch(query, roadmaps, {
      keys: searchKeys,
    });
    return results.map((r) => r.item);
  }, [roadmaps, query, searchKeys]);

  
  const handleInputChange = (e) => {
    setQuery(e.target.value);
  };

  const handleToggle = () => {
    setToggleSearch(prev => {
      const newState = !prev;
      // Clear query when closing
      // if (newState === false) {
      //   setQuery('');
      // }
      return newState;
    });
  };
  
  // Notify parent component whenever filtered results change
  useEffect(() => {
    if (!onFilteredChange) return;
  
    onFilteredChange(prev => {
      // Basic referential check is sufficient now that searchKeys are stable
      if (prev === filteredRoadmaps) return prev;
      return filteredRoadmaps;
    });
  }, [filteredRoadmaps, onFilteredChange]);

  useEffect(() => {
    if(toggleSearch && searchRef?.current) {
      searchRef.current?.focus();
    }
  }, [toggleSearch]);

  return (
    <div
      className={`${cssClass['search-input-container']} ${
        toggleSearch ? cssClass['open'] : ''
      } ${className}`}

      >
      <SearchIcon
        size={20}
        className={cssClass['search-icon']}
        onClick={handleToggle}
        />
      <input
        type="text"
        value={query}
        ref={searchRef}
        placeholder={placeholder}
        onChange={handleInputChange}
        className={cssClass['search-input']}
      />
    </div>
  );
};

const SearchIcon = ({ size = 32, ...props }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      {...props}
    >
      <path
        fill="currentColor"
        d="M9.5 4a6.5 6.5 0 0 1 6.5 6.5c0 1.62-.59 3.1-1.57 4.23l5.65 5.65l-.71.71l-5.65-5.65A6.47 6.47 0 0 1 9.5 17A6.5 6.5 0 0 1 3 10.5A6.5 6.5 0 0 1 9.5 4m0 1A5.5 5.5 0 0 0 4 10.5A5.5 5.5 0 0 0 9.5 16a5.5 5.5 0 0 0 5.5-5.5A5.5 5.5 0 0 0 9.5 5"
      />
    </svg>
  );
};

export default RoadmapsLandingScreen;
